<script setup lang="ts">
const site = useSiteStore()
const store = usePortfolioStore()
const {profile} = store

const status = computed(() => {
  switch(profile?.status) {
    case 0: return 'نامشخص'
    case 1: return 'در جستجوی موقعیت بهتر'
    case 2: return 'فعال'
    case 3: return 'استخدام شده'
  }
})

const facts = [
  { k: 'سن', v: profile?.age || 'نامشخص' },
  { k: 'فریلنس', v: status.value },
  { k: 'آدرس', v: profile?.location || 'نامشخص' }
]
const languages = [
  { name: 'فارسی', level: 100 },
  { name: 'انگلیسی', level: 85 },
  { name: 'عربی', level: 45 }
]

// اگر API مهارت‌ها را نفرستد، نباید کل قالب بشکند.
const profileSkills = computed(() =>
  Array.isArray(profile?.skills) ? profile.skills : []
)
const skills = profileSkills.value.slice(0, 4)
const extras = profileSkills.value.slice(4).map(s => s.title)

const normalizePercent = (value: number | string) =>
  Math.min(100, Math.max(0, Number(value) || 0))

// وضعیت باز/بسته‌ی توضیحات هر مهارت بر اساس ایندکس.
const openSkill = reactive<Record<number, boolean>>({})
const toggleSkill = (index: number) => { openSkill[index] = !openSkill[index] }

// فقط مهارتی که واقعاً توضیح دارد کلید منوی کشویی می‌گیرد.
const hasDescription = (skill: { description?: unknown }) =>
  typeof skill?.description === 'string' && skill.description.trim().length > 0
</script>

<template>
  <!-- بک‌دراپ منوی موبایل -->
  <Teleport to="body">
    <div v-if="site.sideMenuOpen" class="fixed inset-0 z-[60] bg-black/45 backdrop-blur-[2px] md:hidden"
         @click="site.closeSideMenu()" />
  </Teleport>
  <aside class="fixed start-0 top-0 z-[70] h-dvh w-[min(84vw,340px)] shrink-0 overflow-y-auto overscroll-contain
              bg-surface p-6 transition-transform duration-500 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
              md:sticky md:z-auto md:h-auto md:min-h-screen md:max-h-dvh md:w-[320px] md:max-w-[340px] md:translate-x-0 md:transition-none"
         :class="site.sideMenuOpen ? 'translate-x-0 shadow-2xl' : 'translate-x-[105%] md:shadow-none'">
    <div class="relative mx-auto h-[120px] w-[120px]">
      <div class="h-full w-full overflow-hidden rounded-full border-4 border-surface-2">
        <img v-if="profile?.avatar" :src="profile.avatar" :alt="profile?.name" class="h-full w-full object-cover" />
      </div>
      <span title="در دسترس" class="absolute bottom-[7px] end-[7px] h-[15px] w-[15px] rounded-full border-[3px] border-surface bg-green-500"></span>
    </div>
    <h1 class="mt-4 text-center text-lg font-extrabold">{{ profile?.name }}</h1>
    <p class="mt-1.5 text-center text-xs text-muted">{{ profile?.role }}</p>

    <SocialLinks class="mt-4 justify-center" />

    <ul class="mt-6 grid list-none gap-2 p-0 text-xs">
      <li v-for="f in facts" :key="f.k" class="flex items-center justify-between gap-2 border-b border-line pb-2">
        <span class="rounded-[3px] bg-accent px-2 py-0.5 font-bold text-bg-deep">{{ f.k }}</span>
        <span class="text-muted">{{ f.v }}</span>
      </li>
    </ul>

    <!-- <h2 class="mb-3 mt-6 text-[15px] font-extrabold">زبان‌ها</h2>
    <ul class="grid list-none gap-3.5 p-0">
      <li v-for="l in languages" :key="l.name">
        <div class="mb-1.5 flex justify-between text-xs"><span>{{ l.name }}</span><span class="text-muted">{{ l.level }}٪</span></div>
        <div class="h-2.5 overflow-hidden rounded-full border border-line bg-surface-2"><div :data-bar="l.level" class="h-full rounded-full bg-gradient-to-r from-[#ffc95e] to-accent"></div></div>
      </li>
    </ul> -->

    <h2 class="mb-3 mt-6 text-[15px] font-extrabold">مهارت‌ها</h2>
    <ul class="grid list-none gap-3.5 p-0">
      <li v-for="(skill, i) in skills" :key="skill.name">
        <div class="mb-1.5 flex items-center gap-2 text-xs">
          <SkillIcon :icon="skill.icon" class="!h-6 !w-6" />
          <span>{{ skill.title }}</span>
          <span class="ms-auto font-bold text-muted">{{ skill.percent }}٪</span>
          <button
            v-if="hasDescription(skill)"
            type="button"
            :aria-expanded="!!openSkill[i]"
            :title="openSkill[i] ? 'بستن توضیحات' : 'نمایش توضیحات'"
            :aria-label="openSkill[i] ? 'بستن توضیحات' : 'نمایش توضیحات'"
            class="grid h-5 w-5 shrink-0 place-content-center rounded-full border border-line text-muted
                   transition duration-300 hover:border-accent hover:text-accent"
            :class="openSkill[i] ? 'border-accent text-accent' : ''"
            @click="toggleSkill(i)"
          >
            <svg viewBox="0 0 24 24" class="h-3.5 w-3.5 transition-transform duration-300"
                 :class="openSkill[i] ? 'rotate-180' : ''"
                 fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
        </div>
        <div class="h-2.5 overflow-hidden rounded-full border border-line bg-surface-2">
          <div :data-bar="normalizePercent(skill.percent)"
          :style="{width: `${normalizePercent(skill.percent)}%`,}"
          class="h-full rounded-full bg-gradient-to-r from-[#ffc95e] to-accent"></div>
        </div>
        <Transition
          enter-active-class="transition duration-300 ease-out"
          enter-from-class="-translate-y-1 opacity-0"
          leave-active-class="transition duration-200 ease-in"
          leave-to-class="-translate-y-1 opacity-0"
        >
          <p v-if="hasDescription(skill) && openSkill[i]"
             class="mt-2 rounded-lg border border-line bg-surface-2 p-2.5 text-[11px] leading-5 text-muted">
            {{ skill.description }}
          </p>
        </Transition>
      </li>
    </ul>

    <h2 class="mb-3 mt-6 text-[15px] font-extrabold">مهارت‌های تکمیلی</h2>
    <ul class="grid list-none gap-2.5 p-0 text-xs text-muted">
      <li v-for="e in extras" :key="e" class="flex items-center gap-2"><span class="text-accent">↳</span>{{ e }}</li>
    </ul>

    <a :href="profile.cv.url" target="_blank" v-if="profile.cv" class="mt-6 w-full block text-center rounded-lg bg-accent py-3.5 text-[13px] font-extrabold tracking-wide text-bg-deep
                   transition duration-300 hover:-translate-y-0.5 hover:shadow-[var(--t-glow)]">
                   دانلود رزومه ↓
    </a>
  </aside>
</template>
